import { Component, ElementRef, ViewChild, AfterViewInit, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CapicheMaterialModule } from 'capiche-material';
import { ForgotPasswordComponent } from '../forgot-password/forgot-password.component';
import { AuthService } from '../../services/auth/auth.service';
import { SchoolSearchResult, StudentLoginData } from '../../capiche-auth.config';
import { Subject, Subscription, debounceTime, distinctUntilChanged, switchMap, catchError, of } from 'rxjs';

interface LoginForm {
  email: FormControl<string>;
  password: FormControl<string>;
  acceptTerms: FormControl<boolean>;
  name: FormControl<string>;
  refCode: FormControl<string>;
}

interface FormOutput {
  email: string;
  password: string;
  name?: string;
  refCode?: string;
}

@Component({
  selector: 'capiche-auth-login-form',
  imports: [CommonModule, ReactiveFormsModule, FormsModule, CapicheMaterialModule],
  templateUrl: './login-form.component.html',
  styleUrl: './login-form.component.css',
  // Default (Emulated) encapsulation — ViewEncapsulation.None was removed to
  // prevent accidental style leakage into unrelated components.
})
export class LoginFormComponent implements OnInit, AfterViewInit, OnDestroy {
  form!: FormGroup<LoginForm>;
  hidePassword = true;
  //env = environment;

  emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  @Output() formData: EventEmitter<FormOutput> = new EventEmitter();
  /** CSW-1379 follow-up: emitted instead of formData when the dynamic student path is used. */
  @Output() studentFormData: EventEmitter<StudentLoginData> = new EventEmitter();
  /** @deprecated Heading is now in the outer mat-card-header — this input is a no-op. */
  @Input() prompt: string | null = null;
  @Input() msg: string = '';
  @Input() requireAcceptTermsOfUse: boolean = false;

  /** True once the identifier field has content and contains no '@'. Drives the school picker. */
  isUsernameMode = false;
  /** True once the identifier field has been blurred while in username mode — gates rendering
   *  of the school picker so it doesn't appear/shift layout while the user is still typing. */
  revealSchoolPicker = false;
  schoolQuery = '';
  schoolResults: SchoolSearchResult[] = [];
  selectedSchool: SchoolSearchResult | null = null;
  searchingSchools = false;
  /** Student/username login is teacher-managed — there's no self-service reset, so
   *  "Forgot password?" shows an inline hint instead of opening the email dialog. */
  showStudentForgotPasswordHint = false;

  @ViewChild('schoolQueryInput') schoolQueryInput?: ElementRef<HTMLInputElement>;
  /** Register-mode only — conditionally rendered so static:false required. */
  @ViewChild('nameInput') nameInput?: ElementRef<HTMLInputElement>;
  @ViewChild('passwordInput') passwordInput?: ElementRef<HTMLInputElement>;

  private readonly schoolQuery$ = new Subject<string>();
  private subs = new Subscription();

  /** Student path only ever applies to the plain login form, never the sign-up form. */
  get studentModeAvailable(): boolean {
    return !this.requireAcceptTermsOfUse && this.authService.studentLoginAvailable;
  }

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private authService: AuthService,
  ) {}

  /** Track whether the name field has been manually edited by the user. */
  private nameManuallyEdited = false;

  /** Derive a display name from an email local part. */
  private nameFromEmail(email: string): string {
    const local = email.split('@')[0] ?? '';
    return local
      .replace(/[._-]+/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim();
  }

  ngOnInit(): void {
    const refCodeFromUrl = typeof window !== 'undefined'
      ? (new URLSearchParams(window.location.search).get('ref') ?? '')
      : '';

    this.form = this.requireAcceptTermsOfUse
      ? this.fb.group({
          email: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required, Validators.pattern(this.emailRegex)],
          }),
          name: new FormControl<string>('', { nonNullable: true }),
          password: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required],
          }),
          acceptTerms: new FormControl<boolean>(false, {
            nonNullable: true,
            validators: [Validators.requiredTrue],
          }),
          refCode: new FormControl<string>(refCodeFromUrl, { nonNullable: true }),
        })
      : this.fb.group({
          email: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required, Validators.pattern(this.emailRegex)],
          }),
          name: new FormControl<string>('', { nonNullable: true }),
          password: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required],
          }),
          acceptTerms: new FormControl<boolean>(false, { nonNullable: true }),
          refCode: new FormControl<string>('', { nonNullable: true }),
        });

    // Auto-populate name from email prefix in register mode (until user manually edits it)
    if (this.requireAcceptTermsOfUse) {
      this.subs.add(
        this.form.controls['email']!.valueChanges.subscribe((value: string) => {
          if (!this.nameManuallyEdited && value.includes('@')) {
            this.form.controls['name']!.setValue(this.nameFromEmail(value), { emitEvent: false });
          }
        }),
      );
      this.subs.add(
        this.form.controls['name']!.valueChanges.subscribe(() => {
          this.nameManuallyEdited = true;
        }),
      );

      // Pre-fill email (and auto-derive name) from ?email= URL param — set by personalised
      // referral invite links so the recipient doesn't have to retype their address.
      if (typeof window !== 'undefined') {
        const emailParam = new URLSearchParams(window.location.search).get('email');
        if (emailParam) {
          this.form.controls['email']!.setValue(emailParam, { emitEvent: false });
          // Derive name from pre-filled email without marking it as manually edited,
          // so the user can still change the email and have the name update automatically.
          this.form.controls['name']!.setValue(this.nameFromEmail(emailParam), { emitEvent: false });
        }
      }
    }

    if (this.studentModeAvailable) {
      // Char-by-char mode switch: presence of '@' decides email vs. username.
      // No '@' relaxes the email-pattern validator (still required, just not email-shaped).
      this.subs.add(
        this.email!.valueChanges.subscribe((value: string) => {
          const wasUsernameMode = this.isUsernameMode;
          this.isUsernameMode = value.length > 0 && !value.includes('@');

          if (this.isUsernameMode !== wasUsernameMode) {
            this.email!.setValidators(
              this.isUsernameMode ? [Validators.required] : [Validators.required, Validators.pattern(this.emailRegex)],
            );
            this.email!.updateValueAndValidity({ emitEvent: false });
          }

          if (!this.isUsernameMode) {
            this.revealSchoolPicker = false;
            this.schoolResults = [];
            this.schoolQuery = '';
            this.selectedSchool = null;
            this.showStudentForgotPasswordHint = false;
          }
        }),
      );

      this.subs.add(
        this.schoolQuery$
          .pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((q) => {
              if (q.trim().length < 2) {
                this.searchingSchools = false;
                return of([]);
              }
              this.searchingSchools = true;
              return this.authService.searchSchools(q.trim()).then(
                (results) => results,
                () => [],
              );
            }),
          )
          .subscribe((results) => {
            this.schoolResults = results;
            this.searchingSchools = false;

            // CSW-1379 follow-up: a correctly, fully-typed school name shouldn't require
            // also clicking the suggestion — accept an exact (case-insensitive) match
            // automatically, same as if the user had clicked it.
            const typed = this.schoolQuery.trim().toLowerCase();
            if (typed) {
              const exact = results.find((r) => r.name.trim().toLowerCase() === typed);
              if (exact) {
                this.selectedSchool = exact;
                this.schoolResults = [];
              }
            }
          }),
      );
    }
  }

  ngAfterViewInit(): void {
    if (!this.requireAcceptTermsOfUse) return;

    // Smart focus: skip to the first field that still needs input.
    //   email empty          → do nothing (browser focuses first field naturally)
    //   email filled, no name → focus name
    //   email + name filled  → focus password (e.g. arriving via a personalised referral link)
    const email = this.form.controls['email']?.value ?? '';
    const name = this.form.controls['name']?.value ?? '';

    if (email && name) {
      setTimeout(() => this.passwordInput?.nativeElement.focus(), 0);
    } else if (email) {
      setTimeout(() => this.nameInput?.nativeElement.focus(), 0);
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get email() {
    return this.form.get('email');
  }

  get password() {
    return this.form.get('password');
  }

  get acceptTerms() {
    return this.form.get('acceptTerms');
  }

  onIdentifierBlur() {
    if (this.isUsernameMode) {
      this.revealSchoolPicker = true;
    }
  }

  /**
   * CSW-1379 follow-up: the browser resolves Tab's destination element *before* our
   * `blur` handler runs (and the school field, gated by `revealSchoolPicker`, doesn't
   * exist in the DOM yet at that point) — so Tab always landed in the password field
   * instead. Intercept Tab explicitly: reveal the school field now, then move focus
   * into it ourselves on the next tick once Angular has rendered it.
   */
  onIdentifierTab(event: Event) {
    if (!this.isUsernameMode) return;
    event.preventDefault();
    this.revealSchoolPicker = true;
    setTimeout(() => this.schoolQueryInput?.nativeElement.focus(), 0);
  }

  onSchoolQueryChange(value: string) {
    this.schoolQuery = value;
    this.selectedSchool = null;
    this.schoolQuery$.next(value);
  }

  selectSchool(school: SchoolSearchResult) {
    this.selectedSchool = school;
    this.schoolResults = [];
    this.schoolQuery = school.name;
  }

  get canSubmit(): boolean {
    if (this.isUsernameMode) {
      return this.form.controls.password.valid && !!this.email?.value && !!this.selectedSchool;
    }
    return this.form.valid;
  }

  onSubmit() {
    if (!this.canSubmit) return;

    if (this.isUsernameMode && this.selectedSchool) {
      this.studentFormData.emit({
        username: this.email!.value.trim(),
        password: this.password!.value,
        orgId: this.selectedSchool.id,
      });
      return;
    }

    if (this.form.valid) {
      const { email, password, name, refCode } = this.form.value as FormOutput & { name: string; refCode: string };
      const output: FormOutput = { email, password };
      if (this.requireAcceptTermsOfUse) {
        if (name?.trim()) output.name = name.trim();
        if (refCode?.trim()) output.refCode = refCode.trim();
      }
      this.formData.emit(output);
    }
  }

  /**
   * CSW-1379 follow-up: student/username passwords are AES-encrypted and teacher-visible
   * (not bcrypt-hashed) by design — there's no email to send a reset link to, and no
   * self-service reset flow. Route students to "ask your teacher" instead of opening
   * the email-reset dialog.
   */
  onForgotPasswordClick() {
    if (this.isUsernameMode) {
      this.showStudentForgotPasswordHint = true;
      return;
    }
    this.openForgotPasswordDialog();
  }

  openForgotPasswordDialog() {
    this.dialog.open(ForgotPasswordComponent, {
      width: '400px',
      disableClose: true,
    });
  }
}
